import { bnPageHead } from "@/lib/bn-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, Mail, Phone, MapPin, Check, ArrowUpRight } from "lucide-react";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FaqAccordion } from "@/components/FaqAccordion";
import { SITE } from "@/content/site";
import { InlineLeadForm } from "@/components/InlineLeadForm";


const URL = `${SITE.url}/bn/book`;
const TITLE = "ফ্রি স্কোপিং কল বুক করুন | ফ্রিল্যান্সার তামাল";
const DESC =
  "রাহাতুল আশিক তামালের সাথে ২০ মিনিটের ফ্রি এসইও কল বুক করুন। আপনার সাইট ও লক্ষ্য বলুন, সৎ মতামত নিয়ে যান — কোনো বাধ্যবাধকতা নেই।";

export const Route = createFileRoute("/bn/book")({
  head: () =>
    bnPageHead({
      path: "/bn/book",
      enPath: "/book",
      title: TITLE,
      desc: DESC,
      keywords: ["এসইও কনসালটেশন", "ফ্রি এসইও কল", "এসইও বিশেষজ্ঞ যোগাযোগ"],
      suffix: false,
    }),
  component: BnBookPage,
});

const AGENDA = [
  {
    t: "০–৫ মিনিট — আপনি কোথায় আছেন",
    d: "আপনার সাইট, বাজার এবং এ পর্যন্ত কী চেষ্টা করেছেন। কলের আগেই আমি সাইটটা দেখে রাখি, তাই ভূমিকা লাগে না।",
  },
  {
    t: "৫–১২ মিনিট — আমি প্রথমে কী দেখব",
    d: "যে দুই-তিনটি জিনিস সবচেয়ে বেশি আটকে রেখেছে। একসাথে কাজ করি বা না করি, এগুলো আপনি পাবেন।",
  },
  {
    t: "১২–২০ মিনিট — এটা করা যুক্তিসঙ্গত কিনা",
    d: "বাস্তবসম্মত স্কোপ, সময় ও বাজেট। এখন এসইওতে টাকা খরচ করা ঠিক না হলে সেটাও বলে দেব।",
  },
];

const PREP = [
  "আপনার ওয়েবসাইটের ঠিকানা",
  "যে ফলাফলটি চান — কল, অর্ডার, ডেমো নাকি আবেদন",
  "দুই-তিনজন প্রতিযোগীর নাম",
  "Search Console বা GA4 অ্যাক্সেস (থাকলে)",
  "আনুমানিক মাসিক বাজেট — এতে পরামর্শ বদলায়",
];

const FAQ = [
  {
    q: "কলটা কি সত্যিই ফ্রি?",
    a: "হ্যাঁ। ২০ মিনিট, কোনো ফি নেই, কোনো বাধ্যবাধকতা নেই। ভুল প্রজেক্ট শুরু করার চেয়ে ২০ মিনিটে বুঝে নেওয়া ভালো।",
  },
  {
    q: "কোন সময়ে কল করা যায়?",
    a: "আমি বাংলাদেশে (UTC+6)। দেশের ভেতরে অফিস আওয়ারে, আর যুক্তরাষ্ট্র/যুক্তরাজ্য/মধ্যপ্রাচ্যের ক্লায়েন্টদের জন্য সকাল বা রাতেও কল হয়।",
  },
  {
    q: "কলের পরে কী হয়?",
    a: "এক কর্মদিবসের মধ্যে লিখিত সারসংক্ষেপ পাবেন — কোনটা আগে করা উচিত, কোন প্যাকেজ মানানসই এবং দাম কত।",
  },
  {
    q: "কোন ভাষায় কথা হবে?",
    a: "বাংলা বা ইংরেজি — যেটায় স্বচ্ছন্দ।",
  },
];

function BnBookPage() {
  const hasEmbed = Boolean(SITE.booking.url);
  const whatsapp = `https://wa.me/${SITE.phone.replace(/[^0-9]/g, "")}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${URL}#page`,
    url: URL,
    name: TITLE,
    description: DESC,
    inLanguage: "bn-BD",
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { name: "হোম", path: "/bn" },
          { name: "কল বুক করুন", path: "/bn/book" },
        ]}
      />
      <h1 className="sr-only">ফ্রি স্কোপিং কল বুক করুন</h1>
      <JsonLd data={schema} />

      <Section
        eyebrow="কল বুক করুন"
        title={
          <>
            ২০ মিনিট। <span className="text-gradient">সৎ মতামত।</span>
          </>
        }
      >
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          কোনো বিক্রির উপস্থাপনা নয়। আপনি কী বাড়াতে চান বলুন, সার্চ দিয়ে সেটা
          সম্ভব কিনা এবং কতটা খাটুনি লাগবে সেটা আমি বলি।
        </p>

        <div className="mt-12 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            {hasEmbed ? (
              <>
                <div className="rounded-3xl border border-border overflow-hidden bg-card/30">
                  <iframe
                    src={SITE.booking.url}
                    title="বুকিং ক্যালেন্ডার"
                    loading="lazy"
                    className="w-full h-[720px] border-0"
                  />
                </div>
                <div className="mt-6">
                  <InlineLeadForm lang="bn" source="bn-book-page" title="অথবা এখনই ব্রিফ পাঠান" />
                </div>
              </>
            ) : (
              <>
              {/* No calendar connected — the form is the primary path, email/WhatsApp back it up. */}
              <InlineLeadForm
                lang="bn"
                source="bn-book-page"
                title="কলের সময় চেয়ে নিন"
                description="আপনার সাইট ও লক্ষ্য লিখুন। এক কর্মদিবসের মধ্যে আপনার সুবিধামতো দুটি সময় পাঠিয়ে দেব।"
              />
              <div className="mt-6 rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-8 lg:p-10">
                <div className="inline-flex items-center gap-2 text-xs tracking-wide text-primary">
                  <Calendar size={14} /> মেসেজ দিতে চান?
                </div>
                <h2 className="mt-4 font-display text-3xl leading-tight">
                  ইমেইল বা হোয়াটসঅ্যাপেও একইভাবে হবে।
                </h2>
                <p className="mt-4 text-muted-foreground">
                  ইমেইল বা হোয়াটসঅ্যাপই দ্রুততম — সাধারণত এক কর্মদিবসের মধ্যে
                  উত্তর দিই।
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={`mailto:${SITE.email}?subject=%E0%A6%B8%E0%A7%8D%E0%A6%95%E0%A7%8B%E0%A6%AA%E0%A6%BF%E0%A6%82%20%E0%A6%95%E0%A6%B2`}
                    className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium"
                  >
                    <Mail size={16} /> {SITE.email}
                  </a>
                  <a
                    href={whatsapp}
                    target="_blank"
                    rel="noopener"
                    className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:border-primary/50"
                  >
                    <Phone size={16} /> হোয়াটসঅ্যাপ {SITE.phone}
                  </a>
                </div>
                <p className="mt-6 text-xs text-muted-foreground flex items-center gap-2">
                  <MapPin size={13} /> রংপুর, বাংলাদেশ — UTC+6
                </p>
              </div>
              </>
            )}
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl border border-border p-8">
              <h2 className="font-display text-2xl">কলে যা হবে</h2>
              <ul className="mt-6 space-y-5">
                {AGENDA.map((a) => (
                  <li key={a.t}>
                    <div className="text-sm font-medium">{a.t}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{a.d}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-border p-8">
              <h2 className="font-display text-2xl">সাথে রাখুন</h2>
              <ul className="mt-6 space-y-3">
                {PREP.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <Check size={16} className="text-primary mt-0.5 flex-none" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-border p-8">
              <h2 className="font-display text-2xl">আগে দাম জানতে চান?</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                প্রতিটি প্যাকেজের দাম আগেই লেখা আছে।
              </p>
              <Link
                to="/bn/pricing"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:border-primary/50"
              >
                প্যাকেজ ও দাম দেখুন <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section eyebrow="প্রশ্নোত্তর" title="বুক করার আগে।">
        <FaqAccordion items={FAQ} idPrefix="faq-bn-book" />
      </Section>
    </>
  );
}
